import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { selectMetricBannerData } from 'containers/SpearPhishingAgent/selectors'

import Badge from 'components/lib/DesignSystem/V3/Badge'
import Confetti from 'components/lib/DesignSystem/V3/Confetti'

import AwarenessCoverageGauge from './AwarenessCoverageGauge'
import { AiStar, BrandShield, IronscalesMark } from './ConfettiPieces'

// Weighted by repetition (Confetti picks an icon uniformly at random): mostly
// shields, fewer logos. Lower iconRatio keeps most particles plain confetti.
const CONFETTI_PIECES = [
  <IronscalesMark />,
  <BrandShield />,
  <BrandShield />,
  <BrandShield />,
  <AiStar />,
]

const DEFAULT_AWARENESS_COVERAGE_VALUE = 0
const PERIOD_LABEL = 'Last 90 days'

// Once the user has seen the benchmark celebration we persist a flag so the
// confetti never fires again, even across reloads / future visits.
const CONFETTI_SEEN_KEY = 'psima-awareness-benchmark-confetti-seen'

const hasSeenConfetti = () => {
  try {
    return window.localStorage.getItem(CONFETTI_SEEN_KEY) === 'true'
  } catch (e) {
    console.error(e)
    return false
  }
}

const markConfettiSeen = () => {
  try {
    window.localStorage.setItem(CONFETTI_SEEN_KEY, 'true')
  } catch (e) {
    console.error(e)
  }
}

const clamp = (v, min, max) => Math.min(Math.max(v, min), max)

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  position: relative;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Accent = styled.div`
  width: 5px;
  height: 20px;
  border-radius: var(--t-borders-radius-rounded-lg);
  background: var(--t-colors-yellow-400);
`

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
`

const Title = styled.div`
  color: #ffffff;
  font-size: var(--t-typography-font-size-base);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: var(--t-typography-line-height-base);
`

const PeriodTag = styled(Badge)`
  flex-shrink: 0;
`

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  flex: 1 1 auto;
`

const TotalValue = styled.div`
  color: var(--t-colors-base-white);
  font-size: var(--t-typography-font-size-4xl);
  font-weight: var(--t-typography-font-weight-regular);
  line-height: 1;
`

const BenchmarkCopy = styled.div`
  color: #ffffff;
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-typography-font-weight-medium);
  line-height: 16px;
`

const BenchmarkLine = styled.div`
  white-space: normal;
`

const Emphasis = styled.span`
  color: #ffffff;
`

const Muted = styled.span`
  color: rgba(255, 255, 255, 0.6);
`

const GaugeAnchor = styled.div`
  display: flex;
  flex: 0 0 auto;
  /* The trophy cup hangs 18px left of the gauge; reserve that space so it never covers the copy. */
  padding-left: 18px;
`

const AwarenessCoveragePlaceholder = () => {
  const metricBannerData = useSelector(selectMetricBannerData)
  const companyRiskSummaryData = metricBannerData.data?.companyRiskSummary || {}
  const awarenessCoverageData = companyRiskSummaryData.awarenessCoverage || {}
  const value = awarenessCoverageData.coveredPct ?? DEFAULT_AWARENESS_COVERAGE_VALUE
  const benchmarkPct = Number(awarenessCoverageData.benchmarkPct)
  const employeesToBenchmark = awarenessCoverageData.benchmarkDeltaCount
  const coveredCount = awarenessCoverageData.coveredCount
  const totalActiveMailboxes = companyRiskSummaryData.totalActiveMailboxes
  const hasMailboxCounts = Number(totalActiveMailboxes) > 0 && coveredCount != null
  const safeValue = clamp(Math.round(value), 0, 100)
  const isBenchmarkExceeded = Number(value) >= benchmarkPct && benchmarkPct > 0
  const aboveBenchmarkPct = Math.max(Math.round(Number(value) - benchmarkPct), 0)
  const isAtBenchmark = isBenchmarkExceeded && aboveBenchmarkPct === 0

  // Celebrate the first time coverage reaches/passes the Industry Benchmark.
  // We persist a localStorage flag once shown so the confetti fires only once
  // ever — never again on later reloads or visits.
  const [celebrate, setCelebrate] = useState(false)
  const hasCelebratedRef = useRef(false)
  const gaugeRef = useRef(null)

  useEffect(() => {
    if (!metricBannerData?.data) return
    if (hasCelebratedRef.current) return
    if (hasSeenConfetti()) return
    if (isBenchmarkExceeded) {
      hasCelebratedRef.current = true
      markConfettiSeen()
      setCelebrate(true)
    }
  }, [isBenchmarkExceeded, metricBannerData])

  return (
    <Container data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_Placeholder">
      <Confetti
        active={celebrate}
        originRef={gaugeRef}
        icons={CONFETTI_PIECES}
        iconRatio={0.22}
        particleCount={120}
        duration={5}
        fullscreen
        onComplete={() => setCelebrate(false)}
      />
      <Header>
        <Accent />
        <HeaderMain>
          <Title data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_Title_div">
            Awareness Coverage
          </Title>
          <PeriodTag
            dataTestId="Psima_Dashboard_TopBanner_AwarenessCoverage_Period_div"
            color="rgba(255, 255, 255, 0.08)"
            size="small"
            textColor="var(--t-colors-gray-400)"
            variant="filled"
          >
            {PERIOD_LABEL}
          </PeriodTag>
        </HeaderMain>
      </Header>
      <ContentRow>
        <LeftColumn>
          <TotalValue data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_TotalValue_div">{`${safeValue}%`}</TotalValue>
          <BenchmarkCopy>
            {hasMailboxCounts && (
              <BenchmarkLine data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_MailboxesCovered_div">
                <Emphasis>{coveredCount}</Emphasis>
                <Muted> of </Muted>
                <Emphasis>{totalActiveMailboxes}</Emphasis>
                <Muted> mailboxes covered</Muted>
              </BenchmarkLine>
            )}
            {isAtBenchmark ? (
              <BenchmarkLine data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_BenchmarkLine_div">
                <Emphasis>Reached</Emphasis>
                <Muted> the </Muted>
                <Emphasis data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_BenchmarkLine_Emphasis_div">{`${benchmarkPct.toFixed()}%`}</Emphasis>
                <Muted> Industry Benchmark</Muted>
              </BenchmarkLine>
            ) : isBenchmarkExceeded ? (
              <BenchmarkLine data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_BenchmarkLine_div">
                <Emphasis>{`+${aboveBenchmarkPct}% above`}</Emphasis>
                <Muted> the </Muted>
                <Emphasis data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_BenchmarkLine_Emphasis_div">{`${benchmarkPct.toFixed()}%`}</Emphasis>
                <Muted> Industry Benchmark</Muted>
              </BenchmarkLine>
            ) : (
              <BenchmarkLine data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_BenchmarkLine_div">
                <Emphasis>{`${employeesToBenchmark} more`}</Emphasis>
                <Muted> in order to reach </Muted>
                <Emphasis data-testid="Psima_Dashboard_TopBanner_AwarenessCoverage_BenchmarkLine_Emphasis_div">{`${benchmarkPct.toFixed()}%`}</Emphasis>
                <Muted> Industry Benchmark</Muted>
              </BenchmarkLine>
            )}
          </BenchmarkCopy>
        </LeftColumn>
        <GaugeAnchor ref={gaugeRef}>
          <AwarenessCoverageGauge value={safeValue} />
        </GaugeAnchor>
      </ContentRow>
    </Container>
  )
}

export default AwarenessCoveragePlaceholder
